import React from 'react';
import { Link } from 'react-router-dom';
import GuideLayout, { InfoBox, SectionCTA } from '../../components/GuideLayout';

const s = {
  breadcrumb: { fontSize: 13, color: '#94a3b8', marginBottom: 20 },
  breadcrumbLink: { color: '#2563eb', textDecoration: 'none' },
  h1: { fontSize: 32, fontWeight: 800, color: '#0f172a', lineHeight: 1.25, marginBottom: 16, letterSpacing: '-0.4px' },
  lead: { fontSize: 17, color: '#475569', lineHeight: 1.7, marginBottom: 32, borderBottom: '1px solid #e2e8f0', paddingBottom: 32 },
  h2: { fontSize: 22, fontWeight: 700, color: '#0f172a', marginTop: 36, marginBottom: 14, letterSpacing: '-0.2px' },
  h3: { fontSize: 17, fontWeight: 600, color: '#1e293b', marginTop: 24, marginBottom: 10 },
  p: { fontSize: 15, color: '#374151', lineHeight: 1.75, marginBottom: 16 },
  ul: { paddingLeft: 20, marginBottom: 16 },
  li: { fontSize: 15, color: '#374151', lineHeight: 1.75, marginBottom: 6 },
  typeGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: 24 },
  typeCard: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '16px 18px' },
  typeTitle: { fontSize: 14, fontWeight: 700, color: '#1e293b', marginBottom: 6 },
  typeDesc: { fontSize: 13, color: '#64748b', lineHeight: 1.5 },
  nav: { display: 'flex', justifyContent: 'flex-end', marginTop: 48, paddingTop: 24, borderTop: '1px solid #e2e8f0' },
  nextLink: { fontSize: 14, color: '#2563eb', textDecoration: 'none', fontWeight: 600 },
};

export default function WhatIsATender() {
  return (
    <GuideLayout>
      <div style={s.breadcrumb}>
        <Link to="/guide" style={s.breadcrumbLink}>Tender Guide</Link> &rsaquo; What is a public tender?
      </div>

      <h1 style={s.h1}>What is a public tender?</h1>
      <p style={s.lead}>
        A public tender — also called a public contract or procurement notice — is an open invitation from a government body to businesses to submit a proposal for work. It is the standard method by which public money is spent on goods, services, and construction.
      </p>

      <h2 style={s.h2}>Why governments use tenders</h2>
      <p style={s.p}>
        When a public body needs to buy something — whether that's IT services, construction work, medical supplies, or management consulting — it is legally required to run a competitive procurement process above certain spending thresholds. This obligation exists across the EU to ensure public money is spent transparently, fairly, and at good value.
      </p>
      <p style={s.p}>
        The rules prevent governments from simply handing contracts to preferred suppliers. Every qualifying business must have a fair chance to compete. This makes public procurement one of the most level playing fields in B2B sales.
      </p>

      <InfoBox>
        <strong>Key fact:</strong> EU member states must publish contracts above set value thresholds (typically &euro;140,000&ndash;&euro;5.4 million depending on the type of contract and the type of buying authority) on the EU-wide platform so that any business in Europe can see and respond to them.
      </InfoBox>

      <h2 style={s.h2}>The four types of public contracts</h2>
      <p style={s.p}>EU procurement law recognises four categories. Most businesses will encounter the first two:</p>

      <div style={s.typeGrid}>
        <div style={s.typeCard}>
          <div style={s.typeTitle}>Services</div>
          <div style={s.typeDesc}>Consulting, IT development, cleaning, security, training, legal services. The largest category by number of notices.</div>
        </div>
        <div style={s.typeCard}>
          <div style={s.typeTitle}>Supplies</div>
          <div style={s.typeDesc}>Physical goods — medical equipment, office furniture, vehicles, software licences.</div>
        </div>
        <div style={s.typeCard}>
          <div style={s.typeTitle}>Works</div>
          <div style={s.typeDesc}>Construction and civil engineering — buildings, roads, bridges, infrastructure.</div>
        </div>
        <div style={s.typeCard}>
          <div style={s.typeTitle}>Concessions</div>
          <div style={s.typeDesc}>Long-term rights to operate a service or asset — car parks, utilities, port operations.</div>
        </div>
      </div>

      <h2 style={s.h2}>Who publishes tenders?</h2>
      <p style={s.p}>The term "contracting authority" covers a wide range of public bodies:</p>
      <ul style={s.ul}>
        <li style={s.li}><strong>Central government</strong> — ministries, national agencies, tax authorities, defence bodies</li>
        <li style={s.li}><strong>Regional and local government</strong> — municipalities, provinces, counties, city administrations</li>
        <li style={s.li}><strong>Public utilities</strong> — water companies, energy networks, transport operators (subject to their own rules)</li>
        <li style={s.li}><strong>EU institutions</strong> — the European Commission, Parliament, agencies, and programmes</li>
        <li style={s.li}><strong>Healthcare and education</strong> — hospitals, universities, research institutes</li>
      </ul>
      <p style={s.p}>
        In practice this means hundreds of thousands of organisations across Europe are legally obliged to publish their contracts. The market is vast and fragmented — which is exactly why most companies miss opportunities that are directly relevant to them.
      </p>

      <h2 style={s.h2}>What a contract notice looks like</h2>
      <p style={s.p}>A published notice typically includes:</p>
      <ul style={s.ul}>
        <li style={s.li}><strong>Title and description</strong> of the work required</li>
        <li style={s.li}><strong>Contracting authority</strong> name and country</li>
        <li style={s.li}><strong>CPV codes</strong> — standardised classification codes describing the type of contract (see the <Link to="/guide/glossary" style={{ color: '#2563eb' }}>Glossary</Link>)</li>
        <li style={s.li}><strong>Estimated contract value</strong> and duration</li>
        <li style={s.li}><strong>Deadline</strong> for submitting tenders or expressions of interest</li>
        <li style={s.li}><strong>Qualification requirements</strong> — minimum financial turnover, insurance, accreditations</li>
        <li style={s.li}><strong>Award criteria</strong> — how submissions will be scored</li>
      </ul>

      <h2 style={s.h2}>Contract sizes and durations</h2>
      <p style={s.p}>
        Public contracts range from small local service agreements worth &euro;30,000 to multi-year national infrastructure programmes worth hundreds of millions. Most services contracts for consultancy, IT, and professional services fall in the &euro;100,000&ndash;&euro;5 million range and run for one to four years, often with extension options.
      </p>
      <p style={s.p}>
        Many are also structured as <strong>framework agreements</strong> — multi-supplier panels that allow public bodies to call off work without running a full tender each time. Being on a framework is a recurring revenue opportunity, not a one-off sale.
      </p>

      <SectionCTA />

      <div style={s.nav}>
        <Link to="/guide/who-can-bid" style={s.nextLink}>Next: Who can bid? &rarr;</Link>
      </div>
    </GuideLayout>
  );
}
